import React, { useState, useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Col, Container, Row } from "react-bootstrap"
import DataTable from "react-data-table-component"
import CompanySelector from "ATSComponents/Common/CompanySelector"
import OrgTableShimmer from "ATSComponents/Common/OrgTableShimmer"
import axios from "axios"
import { FaClock, FaDesktop, FaNetworkWired, FaUserCheck } from "react-icons/fa"

const reportCardStyle = {
  background: "#fff",
  border: "1px solid #d9e4ef",
  borderRadius: 10,
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.07)",
}

const reportTableStyles = {
  headRow: {
    style: {
      backgroundColor: "#eaf4ff",
      borderTop: "1px solid #d8e6f3",
      borderBottom: "1px solid #c4d8ea",
      minHeight: "42px",
    },
  },
  headCells: {
    style: {
      color: "#0b3a5d",
      fontSize: "11px",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.03em",
      justifyContent: "flex-start",
      paddingLeft: "10px",
      paddingRight: "10px",
      overflow: "visible",
      whiteSpace: "normal",
    },
  },
  rows: {
    style: {
      minHeight: "48px",
      borderBottom: "1px solid #e7edf3",
    },
    stripedStyle: {
      backgroundColor: "#f9fbfd",
    },
  },
  cells: {
    style: {
      color: "#1f2f46",
      fontSize: "12px",
      justifyContent: "flex-start",
      paddingLeft: "10px",
      paddingRight: "10px",
    },
  },
  pagination: {
    style: {
      borderTop: "1px solid #e7edf3",
      minHeight: "48px",
    },
  },
}

const ellipsisCell = value => (
  <span
    title={value || "-"}
    style={{
      display: "block",
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {value || "-"}
  </span>
)

const formatLoginDate = value => {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return date.toLocaleString()
}

const getUserFullName = row =>
  [row.first_name, row.middle_name, row.last_name].filter(Boolean).join(" ").trim() || "-"

const getInitials = name =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .map(part => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?"
const LoginActivityLogs = () => {
  document.title = "ATS - Login Activity Logs";
  const dispatch = useDispatch()
  const { first, rows } = useSelector(state => state.calendar.pagination)
  const userInfos = JSON.parse(sessionStorage.getItem("user-info"))
  const token = userInfos?.access_token
  const initialCompanyId =
    userInfos?.user_details?.companies_data?.[0]?.company_id
  const [totalRows, setTotalRows] = useState(0)
  const [companyId, setCompanyId] = useState(initialCompanyId)
  const [loginActivities, setLoginActivities] = useState([])
  const [loading, setLoading] = useState(false)
  const fetchLoginActivity = async (company_id, page = 1, size = 10) => {
    setLoading(true)
    try {
      const response = await axios.get(
        `/api/v1/login-activity/?company_id=${company_id}&page=${page}&size=${size}`
      )
      if (response.status === 200) {
        setLoginActivities(response?.data?.results || [])
        setTotalRows(response.data.count || 0)
      }
    } catch (error) {
      console.error("Login activity error:", error)
    } finally {
      setLoading(false)
    }
  }
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(10)
  useEffect(() => {
    if (companyId) {
      fetchLoginActivity(companyId, page, size)
    }
  }, [companyId, page, size])
  // useEffect(() => {
  //   if (companyId) {
  //     fetchLoginActivity(companyId)
  //   }
  // }, [companyId])
  const handleCompanyChange = company => {
    if (company?.company_id) {
      setCompanyId(company.company_id)
      setPage(1)
    }
  }
  const loginSummary = useMemo(() => {
    const uniqueUsers = new Set(
      loginActivities.map(item => getUserFullName(item)).filter(name => name !== "-")
    ).size
    const uniqueIps = new Set(loginActivities.map(item => item.ip_address).filter(Boolean)).size
    const latestLogin = loginActivities
      .map(item => item.logged_at)
      .filter(Boolean)
      .sort((a, b) => new Date(b) - new Date(a))[0]

    return {
      total: totalRows,
      uniqueUsers,
      uniqueIps,
      latestLogin: formatLoginDate(latestLogin),
    }
  }, [loginActivities, totalRows])

  const summaryCards = [
    { label: "Total Logs", value: loginSummary.total, icon: <FaUserCheck />, accent: "#2563eb", bg: "#eff6ff" },
    { label: "Users This Page", value: loginSummary.uniqueUsers, icon: <FaDesktop />, accent: "#059669", bg: "#ecfdf5" },
    { label: "IP Addresses", value: loginSummary.uniqueIps, icon: <FaNetworkWired />, accent: "#7c3aed", bg: "#f5f3ff" },
    { label: "Latest Login", value: loginSummary.latestLogin, icon: <FaClock />, accent: "#d97706", bg: "#fffbeb" },
  ]

  const columns = [
    {
      name: "S.No",
      minWidth: "70px",
      grow: 0.45,
      cell: (row, index) => (
        <span style={{ color: "#64748b", fontWeight: 700, fontSize: 12 }}>
          {(page - 1) * size + index + 1}
        </span>
      ),
      sortable: true,
    },
    {
      name: "User Name",
      selector: row => getUserFullName(row),
      cell: row => {
        const name = getUserFullName(row)
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              minWidth: 0,
              width: "100%",
              flexWrap: "nowrap",
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                background: "#e8f2fb",
                color: "#145b84",
                border: "1px solid #cfe2f3",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 800,
                lineHeight: 1,
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              {getInitials(name)}
            </span>
            <div
              style={{
                minWidth: 0,
                fontWeight: 500,
                color: "#0758a8",
                fontSize: 11,
                lineHeight: 1.25,
                textDecoration: "underline",
              }}
            >
              {ellipsisCell(name)}
            </div>
          </div>
        )
      },
      sortable: true,
      center: true,
      minWidth: "240px",
      grow: 1.4,
    },
    {
      name: "IP Address",
      selector: row => row.ip_address || "-" ,
      cell: row => (
        <span
          title={row.ip_address || "-"}
          style={{
            display: "inline-flex",
            alignItems: "center",
            background: "#eef6ff",
            color: "#125986",
            border: "1px solid #cfe5f8",
            borderRadius: 999,
            padding: "3px 9px",
            fontWeight: 700,
            maxWidth: "100%",
            fontSize: 12,
          }}
        >
          {row.ip_address || "-"}
        </span>
      ),
      sortable: true,
      minWidth: "155px",
      grow: 0.9,
    },
    {
      name: "Hostname",
      selector: row => row.hostname || "-",
      cell: row => ellipsisCell(row.hostname),
      sortable: true,
      minWidth: "260px",
      grow: 1.55,
    },
    {
      name: "User Agent",
      selector: row => row.user_agent || "-",
      cell: row => ellipsisCell(row.user_agent),
      sortable: true,
      minWidth: "230px",
      grow: 1.35,
    },
    {
      name: "Logged At",
      selector: row => formatLoginDate(row.logged_at),
      cell: row => (
        <span title={formatLoginDate(row.logged_at)} style={{ fontWeight: 600, fontSize: 12 }}>
          {formatLoginDate(row.logged_at)}
        </span>
      ),
      sortable: true,
      minWidth: "190px",
      grow: 1,
    },
  ]
   const paginationComponentOptions = {
        selectAllRowsItem: true,
        selectAllRowsItemText: "All",
    }

  return (
    <div
      className="page-content allact-tabs"
      style={{
        background: "linear-gradient(180deg, #f3f7fb 0%, #eef4f8 48%, #f8fafc 100%)",
        minHeight: "100vh",
      }}
    >
      <Container fluid={true}>
        <div className="page-title-box actjobbread mb-0">
          <div
            style={{
              borderRadius: 12,
              padding: "20px 22px",
              marginBottom: 16,
              background: "linear-gradient(135deg, #12395c 0%, #0f766e 100%)",
              color: "#fff",
              boxShadow: "0 12px 28px rgba(15, 57, 92, 0.18)",
              overflow: "hidden",
            }}
          >
            <Row className="align-items-center g-3">
              <Col lg={7}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      background: "rgba(255,255,255,0.16)",
                      color: "#fff",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      border: "1px solid rgba(255,255,255,0.22)",
                    }}
                  >
                    <FaUserCheck />
                  </div>
                  <div>
                    <h1 className="page-title mb-1" style={{ fontSize: 23, color: "#fff", fontWeight: 800 }}>
                      Login Activity Report
                    </h1>
                    <div style={{ color: "rgba(255,255,255,0.78)", fontSize: 13 }}>
                      Review user login devices, IP addresses, and access time.
                    </div>
                  </div>
                </div>
              </Col>
              <Col lg={5}>
                <div className="d-flex justify-content-lg-end align-items-center gap-2">
                  <label className="mb-0" style={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.88)" }}>
                    Select Company
                  </label>
                  <div style={{ width: "190px", background: "#fff", borderRadius: 6 }}>
                    <CompanySelector onCompanyChange={handleCompanyChange} />
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          <Row className="g-3 mb-3">
            {summaryCards.map(card => (
              <Col xxl={3} lg={3} md={6} key={card.label}>
                <div
                  style={{
                    ...reportCardStyle,
                    padding: "14px 16px",
                    minHeight: 88,
                    position: "relative",
                    overflow: "hidden",
                    borderTop: `3px solid ${card.accent}`,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
                        {card.label}
                      </div>
                      <div
                        title={String(card.value || "-")}
                        style={{
                          marginTop: 6,
                          color: "#102a43",
                          fontSize: card.label === "Latest Login" ? 14 : 24,
                          fontWeight: 800,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {card.value || "-"}
                      </div>
                    </div>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: card.bg,
                        color: card.accent,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {card.icon}
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          <Row>
            <Col>
              <div style={{ ...reportCardStyle, overflow: "hidden" }}>
                <div
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #e5edf6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    background: "#fff",
                  }}
                >
                  <div style={{ fontWeight: 800, color: "#102a43", fontSize: 15 }}>
                    Login Records
                  </div>
                  <div style={{ color: "#64748b", fontSize: 12 }}>
                    Showing {loginActivities.length} of {totalRows}
                  </div>
                </div>
                <div
                  className="AllLoginActivitysCandy_TableView"
                  id="AllLoginActivitysCandy_TableView"
                  style={{ padding: 0 }}
                >
                  <DataTable
                    columns={columns}
                    data={loginActivities}
                    customStyles={reportTableStyles}
                    progressPending={loading}
                    progressComponent={<OrgTableShimmer columns={5} rows={6} titleWidth="220px" />}
                    pagination
                    paginationServer
                    fixedHeader
                    fixedHeaderScrollHeight="540px"
                    paginationComponentOptions={paginationComponentOptions}
                    paginationTotalRows={totalRows}
                    paginationPerPage={size}
                    onChangePage={newPage => setPage(newPage)}
                    onChangeRowsPerPage={newSize => {
                      setSize(newSize)
                      setPage(1)
                    }}
                    highlightOnHover
                    striped
                    responsive
                    persistTableHead
                  />
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Container>
    </div>
  )
}
export default LoginActivityLogs
